import { Component, ViewChild, OnDestroy } from '@angular/core';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import { TableConfig } from '../../../interfaces/table-config.interface';
import { RenovacionesFilter } from '../../../interfaces/filters.interfaces';
import {
  PolicyRenewalRequest,
  PolicyRenewalResponse,
} from '../../../interfaces/micartera.interfaces';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { MicarteraService } from '../../../services/micartera.service';
import { AuthService } from '../../../services/auth.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { exportarRenovaciones } from '../../../helpers/exportarRenovaciones';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

/**
 * Componente para gestionar las renovaciones de pólizas
 * Permite visualizar y exportar información sobre pólizas que necesitan renovación
 */
@Component({
  selector: 'app-renovaciones',
  standalone: true,
  imports: [
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
    CommonModule,
  ],
  templateUrl: './renovaciones.component.html',
  styleUrls: ['./renovaciones.component.css'],
})
export class RenovacionesComponent implements OnDestroy {
  insuranceData: PolicyRenewalResponse[] = [];
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  errorStatus?: number;
  fechaCorte?: Date;
  title: string = '';
  fecha1?: Date;
  fecha2?: Date;
  lob?: number;
  slob?: number;
  // Configuración de la tabla
  tableConfig: TableConfig = {
    columns: [
      {
        key: 'policyNumber',
        label: 'Póliza',
        sortable: true,
        type: 'poliza',
      },
      {
        key: 'clientCode',
        label: 'Código',
        sortable: true,
      },
      {
        key: 'clientName',
        label: 'Asegurado',
        sortable: true,
      },
      {
        key: 'policyEffectiveDate',
        label: 'Vigencia',
        sortable: true,
        type: 'date', // Mantenemos fecha como texto
      },
      {
        key: 'expirationDate',
        label: 'Fin vigencia',
        sortable: true,
        type: 'date', // Mantenemos fecha como texto
      },
      {
        key: 'premium',
        label: 'Prima anual',
        sortable: true,
        type: 'currency', // Para mostrar como 10,000.00 DOP
      },
      {
        key: 'insuredAmmount',
        label: 'Monto Asegurado',
        sortable: true,
        type: 'currency', // Para mostrar como 10,000.00 DOP
      },

      {
        key: 'balance',
        label: 'Balance',
        sortable: true,
        type: 'currency', // Para mostrar como 10,000.00 DOP
      },
    ],
    showTotals: true,
    totalFields: ['premium', 'balance', 'insuredAmmount'],
  };

  opcionesSelectFilter = [
    { id: 'codigo', nombre: 'Código' },
    { id: 'cedula', nombre: 'Cédula' },
    { id: 'rnc', nombre: 'RNC' },
    { id: 'nombre', nombre: 'Nombre' },
  ];

  // Referencia al componente de la tabla
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  private currentSubscription?: Subscription;

  /**
   * Constructor del componente
   * @param micarteraService Servicio para obtener datos de la cartera
   * @param authService Servicio de autenticación
   */
  constructor(
    private micarteraService: MicarteraService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService
  ) {
    this.title = 'Pólizas a renovar';
  }

  /**
   * Método del ciclo de vida que se ejecuta al destruir el componente
   * Cancela la suscripción activa para evitar memory leaks
   */
  ngOnDestroy() {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
    }
  }

  /**
   * Maneja los cambios en los filtros de búsqueda
   * Realiza una nueva consulta al servidor con los filtros actualizados
   * @param filters Objeto con los filtros seleccionados por el usuario
   */
  onFilterChange(filters: RenovacionesFilter): void {
    // Cancelar cualquier suscripción anterior
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
    }

    if (Object.keys(filters).length === 0) {
      this.insuranceData = [];
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }
    this.fecha1 = filters.dateFrom;
    this.fecha2 = filters.dateTo;

    this.lob = filters.lobId;
    this.slob = filters.sLobId;

    const request: PolicyRenewalRequest = {
      dateFrom: filters.dateFrom || new Date(),
      dateTo: filters.dateTo || new Date(),
      producer: this.authService.getProducerNumber() || 0,
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
    };

    if (filters.lobId) request.lob = filters.lobId;
    if (filters.sLobId) request.sLob = filters.sLobId;
    if (filters.clientCode) request.clientCode = filters.clientCode;
    if (filters.onlyPcyWithBalance)
      request.onlyPcyWithBalance = filters.onlyPcyWithBalance;

    this.currentSubscription = this.micarteraService
      .getPolicyRenewals(request)
      .subscribe({
        next: (
          response: HttpResponse<ApiResponse<PolicyRenewalResponse[]>>
        ) => {
          if (response.status === 200) {
            this.insuranceData = response.body?.data || [];
            this.errorStatus = undefined;
          } else if (response.status === 204) {
            this.insuranceData = [];
            this.mensajeNoData = 'No hay datos disponibles para mostrar';
          }
        },
        error: (error) => {
          console.error('Error al obtener las renovaciones:', error);
          this.insuranceData = [];
          this.mensajeNoData =
            'No se han podido obtener los datos de las renovaciones';
        },
      });
  }

  /**
   * Maneja la exportación de datos en diferentes formatos
   * @param formato Formato de exportación ('pdf' u otros)
   */
  onExportar(formato: string): void {
    if (formato === 'pdf') {
      const params = {
        data: this.insuranceData,
        lob: this.lob,
        slob: this.slob,
        fecha1: this.fecha1!,
        fecha2: this.fecha2!,
        user: this.authService.getProducerName()!,
      };


      //Evento para trackear las exportaciones de archivos en Google Analytics
      this.gAnalytics.trackEvent('renovaciones_exportar',{
        page:'/renovaciones',
        format:'pdf'
      })

      exportarRenovaciones(params);
    } else {
      if (this.tablaComponent) {
        const headerInfo = {
          titulo: 'Pólizas a Renovar',
          fecha1: this.fecha1!,
          fecha2: this.fecha2,

          lob: this.getLobName(),
          slob: this.getSlobName(),
          tableConfig: this.tableConfig,
        };

        //Evento para trackear las exportaciones de archivos en Google Analytics
      this.gAnalytics.trackEvent('renovaciones_exportar',{
        page:'/renovaciones',
        format:formato
      })
        this.tablaComponent.exportarDatos(formato, headerInfo);
      }
    }
  }

  // Extrayendo los nombres de los ramos y subramos
  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo (LOB) seleccionado
   * @returns Nombre del ramo o cadena vacía si no hay selección
   */
  public getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo (SLOB) seleccionado
   * @returns Nombre del subramo o cadena vacía si no hay selección
   */
  public getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }
}
